import * as v from 'valibot';

/**
 * How a connection gets its credentials at call time. This is the crux of
 * reef's access model: an MCP connection shared on a workflow is a *pointer*,
 * not a grant. What actually authorizes the call is decided here.
 *
 * - `user_oauth`  — the calling user's own OAuth token. If they never
 *                   authorized this provider, the call fails and reef shows the
 *                   connect prompt. Sharing a workflow never widens data access.
 * - `service`     — a workspace-level service credential (LaunchDarkly, Signoz).
 *                   Any user who can run the workflow uses it, so these are
 *                   admin-managed and audited by connection id.
 * - `user_or_service` — both are available and the step says which. Unattended
 *                   runs fall back to the bot so they keep working after the
 *                   triggering human logs off; a person pressing Run acts as
 *                   themselves, so the comment carries their name. See
 *                   `ConnectionActor`.
 * - `github_app`  — GitHub App installation token, for automatic steps. The
 *                   same rule as `user_or_service`, with the App standing in for
 *                   the service credential.
 * - `system`      — reef's own control-plane credentials. Never exposed to a step.
 */
export const CredentialMode = v.picklist([
  'user_oauth',
  'service',
  'user_or_service',
  'github_app',
  'system',
]);
export type CredentialMode = v.InferOutput<typeof CredentialMode>;

/**
 * Which identity a step calls a dual-identity connection with.
 *
 * `auto` is the rule reef has always applied to GitHub: the acting human when
 * one pressed the button, the workspace bot when nobody did. The two explicit
 * values exist because "who does this comment come from" is a product decision,
 * not a scheduling detail — a triage note should read as the bot even when a
 * human triggered the run, and a reply in someone's voice should never silently
 * become the bot's because the run happened to be unattended.
 *
 * Pinning `user` on a step that can fire unattended is legal and will fail
 * closed with a connect prompt rather than quietly falling back to the bot.
 */
export const ConnectionActor = v.picklist(['auto', 'user', 'service']);
export type ConnectionActor = v.InferOutput<typeof ConnectionActor>;

/**
 * The eight connectors reef shipped with, kept as a list rather than a closed
 * type. `kind` is open now: a connector is a registry manifest, and a picklist
 * here would mean adding ClickHouse required a schema change, a migration and a
 * deploy — the exact coupling the registry exists to remove.
 *
 * Validation moved with the data: `@reef/connectors` checks `provider` against
 * the registry at the point a connection is registered, where there is a
 * manifest to check it against.
 */
export const BUILTIN_CONNECTION_KINDS = [
  'postgres',
  'github',
  'linear',
  'slack',
  'launchdarkly',
  'signoz',
  'http',
  'custom',
] as const;
export type ConnectionKind = string;

/**
 * How a credential is *obtained*, as distinct from `CredentialMode`, which says
 * whose it is. A `user_oauth` connection can get the caller's token from
 * better-auth (a provider configured at deploy time) or by discovering an
 * arbitrary MCP server's authorization server and registering with it; the
 * access model is identical and the machinery is not.
 */
export const AuthMode = v.picklist(['better_auth', 'dynamic', 'secret', 'github_app', 'none']);
export type AuthMode = v.InferOutput<typeof AuthMode>;

/**
 * How much a connector is trusted, which is a property of how it got here — not
 * of what it does. `byo` is "somebody pasted a URL", and it is the tier that
 * never auto-applies a write.
 */
export const TrustTier = v.picklist(['system', 'vetted', 'byo']);
export type TrustTier = v.InferOutput<typeof TrustTier>;

export const ConnectionDef = v.object({
  id: v.string(),
  name: v.string(),
  /** Registry key. Validated against `@reef/connectors` at registration. */
  provider: v.string(),
  /** Legacy display kind. Equal to `provider` for anything registered since the registry. */
  kind: v.optional(v.string()),
  /** MCP server endpoint. Reef only ever speaks MCP to a connection. */
  mcpUrl: v.string(),
  transport: v.optional(v.picklist(['http', 'sse']), 'http'),
  credentialMode: CredentialMode,
  authMode: v.optional(AuthMode, 'better_auth'),
  trustTier: v.optional(TrustTier, 'vetted'),
  /**
   * Resource-URL grants. Empty means the connection was never scoped, which is
   * how everything registered before the connector framework is configured —
   * the manifest decides whether that is acceptable, once, at registration.
   */
  scope: v.optional(v.array(v.string()), []),
  /**
   * SHA-256 of the tool catalog as approved. A refresh that returns a different
   * one is drift, and drift forces re-approval rather than being adopted.
   */
  catalogFingerprint: v.optional(v.nullable(v.string())),
  /** Whatever the manifest's configurator form collected — a service name, a region, a URL. */
  configurator: v.optional(v.nullable(v.record(v.string(), v.unknown()))),
  /**
   * OAuth provider key in better-auth, for `user_oauth` and `user_or_service`
   * connections. Used to look up the caller's token and, when missing, to build
   * the connect link.
   */
  oauthProvider: v.optional(v.nullable(v.string())),
  /**
   * Secret name in the Worker's environment for `service` and `user_or_service`
   * connections. The value never lands in D1 or in a workflow document.
   */
  secretRef: v.optional(v.nullable(v.string())),
  /**
   * How the bot identity signs its work, shown wherever reef has to explain
   * whose name will appear — a Linear comment posted by the workspace token
   * reads as this, not as the person who pressed Run.
   */
  serviceAccountLabel: v.optional(v.nullable(v.string())),
  /** Hosts a step gains when it mounts this connection, on top of its own allowlist. */
  egressHosts: v.optional(v.array(v.string()), []),
  /** Default policy applied to every step that mounts this connection. */
  defaultPolicyId: v.optional(v.nullable(v.string())),
  createdBy: v.optional(v.string()),
  createdAt: v.optional(v.string()),
});
export type ConnectionDef = v.InferOutput<typeof ConnectionDef>;

/**
 * Parse a connection, filling `provider` from `kind` when it is missing.
 *
 * Rows written before the registry have a `kind` and no `provider`. Backfilling
 * here rather than only in the migration means a document, a seed script, or an
 * API caller that still speaks the old shape keeps working — and lands on the
 * `custom` manifest, which is the `byo` tier, which is the safe reading of
 * "reef does not know what this is".
 */
export function parseConnection(input: unknown): ConnectionDef {
  const record = (input ?? {}) as Record<string, unknown>;
  return v.parse(ConnectionDef, {
    ...record,
    provider: record.provider ?? record.kind,
    kind: record.kind ?? record.provider,
  });
}

/**
 * Raised when a user triggers a workflow that mounts a connection they have not
 * authorized. Carries everything the UI needs to render the "[Connect]" button
 * without a second round trip.
 */
export class ConnectionAccessError extends Error {
  readonly connectionId: string;
  readonly connectionName: string;
  readonly oauthProvider: string | null;
  readonly connectUrl: string | null;

  constructor(options: {
    connectionId: string;
    connectionName: string;
    oauthProvider?: string | null;
    connectUrl?: string | null;
  }) {
    super(
      `This workflow uses the ${options.connectionName} connection, which you haven't authenticated to.`,
    );
    this.name = 'ConnectionAccessError';
    this.connectionId = options.connectionId;
    this.connectionName = options.connectionName;
    this.oauthProvider = options.oauthProvider ?? null;
    this.connectUrl = options.connectUrl ?? null;
  }

  toJSON() {
    return {
      error: 'connection_not_authorized' as const,
      message: this.message,
      connectionId: this.connectionId,
      connectionName: this.connectionName,
      oauthProvider: this.oauthProvider,
      connectUrl: this.connectUrl,
    };
  }
}

/**
 * Raised when a connection is blocked by something the *caller* cannot fix — a
 * bot credential the workspace never configured, most often.
 *
 * Kept distinct from `ConnectionAccessError` because the remedy is different and
 * the UI has to say so: a Connect button is useless when the missing thing is a
 * secret only an admin can set. Both are collected by `preflightConnections`, so
 * a run still reports every blocker at once.
 */
export class ConnectionUnavailableError extends Error {
  readonly connectionId: string;
  readonly connectionName: string;
  readonly secretRef: string | null;

  constructor(options: {
    connectionId: string;
    connectionName: string;
    secretRef?: string | null;
    message?: string;
  }) {
    super(
      options.message ??
        `The ${options.connectionName} connection has no bot credential configured on this deployment. A workspace admin needs to set it.`,
    );
    this.name = 'ConnectionUnavailableError';
    this.connectionId = options.connectionId;
    this.connectionName = options.connectionName;
    this.secretRef = options.secretRef ?? null;
  }

  toJSON() {
    return {
      error: 'connection_unavailable' as const,
      message: this.message,
      connectionId: this.connectionId,
      connectionName: this.connectionName,
      // Named so the UI can render the same wording an admin will search for,
      // and null-safe so a secret *name* is all that ever leaves the Worker.
      secretRef: this.secretRef,
      oauthProvider: null,
      connectUrl: null,
    };
  }
}
